import { Controller } from '@hotwired/stimulus'

// Зазор между стрелкой и карточками — тот же, что у левой стрелки (-left-12
// в show.slim: 48px = w-9 + 12px).
const ARROW_GAP = 12

/**
 * @file Страница сравнения (4-10 товаров, см. ComparesController::
 * INDEXABLE_ITEMS) не сжимает колонки в нечитаемую ширину, а скроллит их
 * горизонтально — но карточки, рейтинги и характеристики размечены как
 * отдельные строки таблицы, и без синхронизации у каждой свой независимый
 * скролл: докрутил карточки до последней ракетки — а её рейтинги и
 * характеристики остались там же, что были.
 *
 * Все track-элементы ДОЛЖНЫ давать одинаковую ширину карточки, иначе один и
 * тот же scrollLeft сдвигал бы разные строки на разное число ракеток. Ruby
 * рендерит одну и ту же %-формулу (grid_cols_style в compares/show.slim) во
 * все три, но "100%" в ней — это ширина СОБСТВЕННОГО контейнера каждого
 * track, а не общая: у карточек он лежит прямо в .container, у рейтингов и
 * характеристик — внутри своей же карточки с паддингом, поэтому натурально
 * получаются РАЗНЫЕ px. Чиним тем, что меряем эталон один раз по первому
 * track (карточки) и потом принудительно ставим этот же px везде (см.
 * #clampTrackWidth) — дальше scrollWidth действительно совпадает пиксель в
 * пиксель, и можно просто зеркалить scrollLeft.
 *
 * Стрелки (prevArrow/nextArrow) — подсказка, что слева/справа есть ещё
 * товары: они лежат только над карточками, но раз все track скроллятся
 * синхронно, для решения "есть куда скроллить ещё" достаточно состояния
 * первого трека (карточки).
 */
export default class CompareScrollSyncController extends Controller {
  static targets = ['track', 'prevArrow', 'nextArrow']

  // Ruby рендерит grid-template-columns один раз, в HTML (см. grid_cols_style
  // в show.slim) — сюда складываем эту исходную %-формулу до первой правки,
  // чтобы на resize было к чему возвращаться перед новым замером (см.
  // #clampTrackWidth: сам мутирует это свойство на фиксированный px).
  #originalColumns = new WeakMap()

  connect() {
    this.trackTargets.forEach((track) => {
      track.addEventListener('scroll', this.#onScroll, { passive: true })
      const grid = track.firstElementChild
      if (grid && !this.#originalColumns.has(grid)) {
        this.#originalColumns.set(grid, grid.style.gridTemplateColumns)
      }
    })
    window.addEventListener('resize', this.#handleResize)
    this.#clampTrackWidths()
    this.#updateArrow()
  }

  disconnect() {
    this.trackTargets.forEach((track) => {
      track.removeEventListener('scroll', this.#onScroll)
    })
    window.removeEventListener('resize', this.#handleResize)
    this.#cancelScrollAnimation()
  }

  scrollPrev() {
    const track = this.trackTargets[0]
    if (!track) return

    this.#animateScrollBy(track, -this.#itemPitch(track))
  }

  scrollNext() {
    const track = this.trackTargets[0]
    if (!track) return

    this.#animateScrollBy(track, this.#itemPitch(track))
  }

  // Свой rAF-анимированный скролл вместо scrollBy({behavior: 'smooth'}):
  // нативная "плавность" привязана к дистанции — у крупных карточек товаров
  // (тут) шаг заметно больше, чем у компактных слотов конструктора (см.
  // compare_picker_controller.js), и та же "smooth" на большем расстоянии
  // ощущалась рывком, а не плавным скроллом. Фиксированная длительность —
  // одинаковое ощущение скорости независимо от размера карточки.
  //
  // Ид кадра храним, чтобы второй клик по стрелке не запускал параллельный
  // цикл (два цикла перезаписывали бы scrollLeft друг друга), а disconnect
  // не оставлял анимацию жить после ухода контроллера.
  #scrollFrame = null

  #animateScrollBy(track, delta) {
    this.#cancelScrollAnimation()

    // mandatory-снап (см. .compare-scroll-track) корректирует программный
    // scrollLeft к ближайшей точке — промежуточные кадры rAF он превращал
    // бы в рывки, поэтому на время анимации выключаем его на всех треках
    // (зеркалирование в #onScroll трогает и их); конечная точка и так
    // кратна карточке, после восстановления снапу нечего доводить.
    this.trackTargets.forEach((other) => {
      other.style.scrollSnapType = 'none'
    })

    const duration = 350
    const start = track.scrollLeft
    const startTime = performance.now()
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2)

    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / duration)
      track.scrollLeft = start + delta * ease(progress)
      if (progress < 1) {
        this.#scrollFrame = requestAnimationFrame(step)
      } else {
        this.#finishScrollAnimation()
      }
    }

    this.#scrollFrame = requestAnimationFrame(step)
  }

  #cancelScrollAnimation() {
    if (this.#scrollFrame === null) return

    cancelAnimationFrame(this.#scrollFrame)
    this.#finishScrollAnimation()
  }

  #finishScrollAnimation() {
    this.#scrollFrame = null
    this.trackTargets.forEach((other) => {
      other.style.scrollSnapType = ''
    })
  }

  #handleResize = () => {
    this.#clampTrackWidths()
    this.#updateArrow()
  }

  // Ширина колонок (grid_cols_style в show.slim) пинится к "как в сравнении
  // на 4" — но при произвольной ширине экрана видимая область почти никогда
  // не делится на карточки без остатка, и последняя карточка повисала бы
  // обрезанной точно под стрелкой. Подгоняем ширину карточки так, чтобы
  // целое число карточек ровно заняло видимую область (см.
  // #referenceCardSize): ни одна не рисуется наполовину, и справа не остаётся
  // пустой полосы почти в карточку шириной.
  #clampTrackWidths() {
    const reference = this.#referenceCardSize()
    this.trackTargets.forEach((track) =>
      this.#clampTrackWidth(track, reference)
    )
    this.#positionNextArrow()
  }

  // Правая стрелка — на том же расстоянии от карточек, что и левая (12px,
  // см. -left-12 у prevArrow в show.slim). Классом это не задать: track
  // карточек подрезается по самому узкому из треков (см. #clampTrackWidth) и
  // заканчивается раньше своего контейнера, а absolute-стрелка отсчитывается
  // от края контейнера — с -right-14 между ней и последней карточкой
  // оставалось бы 44px. Ставим right от фактического края track.
  #positionNextArrow() {
    const track = this.trackTargets[0]
    if (!track || !this.hasNextArrowTarget) return

    const container = track.parentElement
    const trackGap =
      container.clientWidth - (track.offsetLeft + track.offsetWidth)
    const arrowWidth = this.nextArrowTarget.offsetWidth || 36
    this.nextArrowTarget.style.right = `${trackGap - ARROW_GAP - arrowWidth}px`
  }

  // Эталонная ширина карточки — по первому track (карточки товаров): он
  // лежит прямо в .container, без дополнительного паддинга оборачивающей
  // карточки, как у рейтингов/характеристик, поэтому естественная %-формула
  // на нём и есть "как в сравнении на 4" в чистом виде.
  //
  // Число целиком видимых карточек (wholeCount) — одно на все track, по
  // САМОМУ УЗКОМУ из них: обёртки рейтингов и характеристик уже строки
  // карточек (паддинг карточки-обёртки, у рейтингов шкалы не заходят за
  // разделитель — см. show.slim). Свой счёт у каждого track у границы
  // Math.floor разошёлся бы на одну карточку — треки получили бы разную
  // ширину и разный максимум scrollLeft, а зеркалирование одного и того же
  // значения (см. #onScroll) оставляло бы последнюю колонку какой-то из строк
  // недокрученной. По самому узкому — чтобы в нём последняя колонка не
  // обрезалась.
  #referenceCardSize() {
    const track = this.trackTargets[0]
    const grid = track?.firstElementChild
    if (!grid || grid.children.length < 2) return null

    this.trackTargets.forEach((other) => {
      other.style.maxWidth = ''
    })
    if (this.#originalColumns.has(grid)) {
      grid.style.gridTemplateColumns = this.#originalColumns.get(grid)
    }

    if (grid.scrollWidth <= track.clientWidth + 1) return null

    const availableWidth = Math.min(
      ...this.trackTargets.map((other) => other.clientWidth)
    )

    const pitch = this.#itemPitch(track)
    const cardWidth = grid.children[0].getBoundingClientRect().width
    if (pitch <= 0) return null

    const gap = pitch - cardWidth
    // Сколько карточек видно целиком — ближайшее целое, а не floor: при
    // floor четвёртая карточка, которой не хватило нескольких пикселей,
    // оставляла справа пустую полосу почти в карточку шириной. Карточки
    // слегка сжимаются или растягиваются (не больше чем на полкарточки на
    // весь ряд) и заполняют видимую область ровно.
    const wholeCount = Math.min(
      grid.children.length,
      Math.max(1, Math.round((availableWidth + gap) / pitch))
    )
    // floor — чтобы сумма колонок не вылезла за availableWidth на долю
    // пикселя и последняя карточка не обрезалась.
    const fittedWidth = Math.floor(
      (availableWidth - gap * (wholeCount - 1)) / wholeCount
    )

    return { cardWidth: fittedWidth, gap, wholeCount }
  }

  #clampTrackWidth(track, reference) {
    const grid = track.firstElementChild
    if (!grid || grid.children.length < 2) return

    // Сбрасываем перед измерением: на resize track.clientWidth иначе
    // отражает уже применённое на предыдущем проходе ограничение, а не то,
    // сколько места реально доступно теперь.
    track.style.maxWidth = ''

    // 2-3 товара и так делят ширину поровну (см. INDEXABLE_ITEMS), эталон
    // тогда не считается (#referenceCardSize вернёт null) — оставляем
    // нативный гибкий grid как есть, ничего не фиксируем.
    if (!reference) {
      if (this.#originalColumns.has(grid)) {
        grid.style.gridTemplateColumns = this.#originalColumns.get(grid)
      }
      return
    }

    // Фиксируем ширину колонки эталонным px — не своим собственным %-based
    // значением (оно у рейтингов/характеристик естественно меньше из-за
    // паддинга их карточки-обёртки), иначе одна и та же scrollLeft сдвигала
    // бы разные строки на разное число ракеток. wholeCount по той же причине
    // общий (см. #referenceCardSize): у всех треков одинаковые и ширина, и
    // максимум scrollLeft — зеркалирование сходится на обоих краях.
    const { cardWidth, gap, wholeCount } = reference
    const pitch = cardWidth + gap
    const totalCards = grid.children.length
    grid.style.gridTemplateColumns = `repeat(${totalCards}, ${cardWidth}px)`

    // max-width, а не width: на десктопе track — растущий flex-элемент рядом
    // с колонкой подписей (см. compares/show.slim), и width у flex: 1 1 0%
    // не действует; max-width ограничивает его и в flex, и в обычном блоке.
    if (wholeCount < totalCards) {
      track.style.maxWidth = `${wholeCount * pitch - gap}px`
    }
  }

  // Шаг проматывания — ширина одной карточки вместе с gap до соседней (не
  // просто её собственная ширина: без учёта gap стрелка недокручивала бы до
  // ровного края). Меряем по факту через offsetLeft соседних карточек, а не
  // читаем gap из CSS — тогда работает при любом брейкпоинте без дублирования
  // самого значения gap здесь.
  #itemPitch(track) {
    const grid = track.firstElementChild
    if (!grid || grid.children.length < 2) return track.clientWidth

    return grid.children[1].offsetLeft - grid.children[0].offsetLeft
  }

  // Флаг, а не отписка/подписка: под rAF синхронный "scroll" от программной
  // установки scrollLeft ниже пришёл бы на те же track и снова дёрнул бы
  // обработчик — без флага скролл двух и более строк разошёлся бы по кругу.
  #syncing = false

  #onScroll = (event) => {
    if (this.#syncing) return

    this.#syncing = true
    const { scrollLeft } = event.target
    this.trackTargets.forEach((track) => {
      if (track !== event.target) track.scrollLeft = scrollLeft
    })
    this.#syncing = false
    this.#updateArrow()
  }

  // style.display, а не класс "hidden": строка сравнения уже держит на этой
  // кнопке постоянные "flex items-center justify-center" для иконки — переключай
  // видимость классом "hidden" рядом с ними, и итог зависел бы от порядка
  // утилит в сгенерированном Tailwind CSS, а не от того, какой класс добавлен
  // последним.
  #updateArrow = () => {
    const track = this.trackTargets[0]
    if (!track) return

    if (this.hasPrevArrowTarget) {
      this.prevArrowTarget.style.display =
        track.scrollLeft > 1 ? 'flex' : 'none'
    }
    if (this.hasNextArrowTarget) {
      const canScrollMore =
        track.scrollWidth - track.clientWidth - track.scrollLeft > 1
      this.nextArrowTarget.style.display = canScrollMore ? 'flex' : 'none'
    }
  }
}
